import { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import {
  ModalContainer,
  ModalBody,
  ModalTitle,
  ModalHeader,
  InjectedModalProps,
  Button,
  AutoRenewIcon,
  // TrophyGoldIcon,
  Text,
  Flex,
  Heading,
  // Box,
  ModalCloseButton,
  Skeleton,
} from 'uikit'
import { AnyAction, AsyncThunkAction } from '@reduxjs/toolkit'
import { Token } from 'mojitoswap-sdk'

import { useWeb3React } from '@web3-react/core'
import { REWARD_RATE } from 'state/predictions/config'
import { fetchNodeHistory, markAsCollected, fetchLedgerData } from 'state/predictions'
import { Bet } from 'state/types'
import { useTranslation } from 'react-i18next'
import useToast from 'hooks/useToast'
import { usePredictionsContract } from 'hooks/useContract'
import { useCallWithGasPrice } from 'hooks/useCallWithGasPrice'
import useCatchTxError from 'hooks/useCatchTxError'
import { formatNumber } from 'utils/formatBalance'
import { getPayout } from './History/helpers'
import { useResponsive } from 'hooks/useResponse'
import BN from 'bignumber.js'
import QRCode from 'qrcode.react'
import { shareUrl } from 'constants/referral'
import html2canvas from 'html2canvas'
import { FacebookShareButton, TelegramShareButton } from 'react-share'
import copy from 'copy-to-clipboard'

interface CollectRoundWinningsModalProps extends InjectedModalProps {
  onSuccess?: () => Promise<void>
  dispatch: (action: AnyAction | AsyncThunkAction<any, { account: string }, any>) => void
  history: Bet[]
  isLoadingHistory: boolean
  predictionsAddress: string
  token: Token
  code?: string
}

const Imgs = {
  telegram: require('../../../assets/images/prediction/telegram.png').default,
  twitter: require('../../../assets/images/prediction/twitter.png').default,
  link: require('../../../assets/images/prediction/share.png').default,
  facebook: require('../../../assets/images/prediction/facebook.png').default,
  download: require('../../../assets/images/prediction/download.png').default,
  bg: require('../../../assets/images/prediction/post-bg.png').default,
  logo: require('../../../assets/images/prediction/logo.png').default,
}

const Modal = styled(ModalContainer)`
  overflow: visible;
  width: calc(100vw - 16px);
  margin-top: -20px;
  ${({ theme }) => theme.mediaQueries.md} {
    min-width: ${({ minWidth }) => minWidth};
    width: 548px;
    max-width: 548px;
    margin-top: -50px;
  }
`

const ImgCup = styled.img`
  width: 138px;
  height: 92px;
  margin: 0 auto;
`

const ShareBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0 0;
  flex-direction: column;
  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
  }
`

const ImgShare = styled.img`
  height: 40px;
  width: 40px;
  cursor: pointer;
  &:hover {
    opacity: 0.75;
  }
`

const PostContainer = styled.div`
  background: #f3f3f3;
  padding: 12px;
  max-height: 68vh;
  overflow-y: scroll;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 28px 54px;
  }
`

const PostBg = styled.div`
  width: 100%;
  // min-height: 475px;
  background: white;
  position: relative;
  padding: 20px 24px 0 24px;
  // ${({ theme }) => theme.mediaQueries.md} {
  //   min-height: 570px;
  // }
`

const PostBottom = styled.div`
  background: linear-gradient(91.72deg, #14d89d 1.03%, #0ac4dd 102.03%);
  height: 128px;
  width: 100%;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  box-sizing: content-box;
  padding: 0 24px;
  margin: 24px 0 0 -24px;
  ${({ theme }) => theme.mediaQueries.md} {
    height: 150px;
  }
`

const PostImgBg = styled.img`
  width: 153px;
  height: 196px;
  position: absolute;
  right: 0;
  bottom: 128px;
  ${({ theme }) => theme.mediaQueries.md} {
    width: 206px;
    height: 265px;
    bottom: 150px;
  }
`

const ImgLogo = styled.img`
  height: 36px;
`

const Divider = styled.div`
  height: 32px;
  width: 1px;
  background: rgba(115, 126, 141, 0.16);
  margin: 0 15px;
`

interface ClaimableRounds {
  epochs: number[]
  total: number
  joinAmount: number
}

const calculateClaimableRounds = (history): ClaimableRounds => {
  if (!history) {
    return { epochs: [], total: 0, joinAmount: 0 }
  }

  return history.reduce(
    (accum: ClaimableRounds, bet: Bet) => {
      if (!bet.claimed && bet.position === bet.round.position) {
        const betPayout = getPayout(bet, REWARD_RATE)
        return {
          ...accum,
          epochs: [...accum.epochs, bet.round.epoch],
          total: accum.total + betPayout,
          joinAmount: accum.joinAmount + bet.amount,
        }
      }

      return accum
    },
    { epochs: [], total: 0, joinAmount: 0 }
  )
}

const CollectRoundWinningsModal: React.FC<CollectRoundWinningsModalProps> = ({
  onDismiss,
  onSuccess,
  history,
  isLoadingHistory,
  dispatch,
  predictionsAddress,
  token,
  code,
}) => {
  const { account } = useWeb3React()
  const { t } = useTranslation()
  const { toastSuccess, toastInfo } = useToast()
  const { fetchWithCatchTxError, loading: isPendingTx } = useCatchTxError()
  const { callWithGasPrice } = useCallWithGasPrice()
  const predictionsContract = usePredictionsContract(predictionsAddress, token.symbol)

  const { epochs, total, joinAmount } = calculateClaimableRounds(history)
  const [showPost, setShowPost] = useState(false)
  const { isMobile } = useResponsive()
  const isLoading = isLoadingHistory || !epochs?.length
  const referralCode = useRef('')

  useEffect(() => {
    if (code && !referralCode.current) {
      referralCode.current = code
    }
  }, [code, referralCode])

  useEffect(() => {
    // Fetch history if they have not opened the history pane yet
    if (account) {
      dispatch(fetchNodeHistory({ account }))
    }
  }, [account, dispatch])

  const handleClick = async () => {
    const receipt = await fetchWithCatchTxError(() => {
      return callWithGasPrice(predictionsContract, 'claim', [epochs])
    })
    if (receipt?.status) {
      // Immediately mark rounds as claimed
      await dispatch(
        markAsCollected(
          epochs.reduce((accum, epoch) => {
            return { ...accum, [epoch]: true }
          }, {})
        )
      )

      await dispatch(
        fetchLedgerData({
          account,
          epochs,
        })
      )

      if (onSuccess) {
        await onSuccess()
      }
      toastSuccess(
        t('Winnings collected!')
        // <ToastDescriptionWithTx txHash={receipt.transactionHash as any}>
        //   {t('Your prizes have been sent to your wallet')}
        // </ToastDescriptionWithTx>
      )
      onDismiss()
    }
  }

  const downloadCurrentImage = (picUrl: string) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = image.width
      canvas.height = image.height
      const context = canvas.getContext('2d') as CanvasRenderingContext2D
      context.drawImage(image, 0, 0, image.width, image.height)
      const url = canvas.toDataURL('image/png')
      const a = document.createElement('a')
      a.download = 'mojito_referral_pic.png'
      a.href = url
      a.click()
    }
    image.src = picUrl
  }

  const exportImg = () => {
    html2canvas(document.getElementById(`post-card`) as HTMLElement, {
      useCORS: true,
    }).then((canvas) => {
      const imgUrl = canvas.toDataURL('image/png')
      downloadCurrentImage(imgUrl)
    })
  }

  const clickShare = (type: string) => {
    let url: string
    if (type === 'twitter') {
      url = `https://twitter.com/intent/tweet?text=Just won a prediction on @MojitoSwap! The prediction market is so much fun - let's try it together! Participate in MojitoSwap's prediction market and earn even more rewards! Learn More: &url=${shareUrl}?code=${referralCode.current}&hashtags=DeFi&hashtags=KCC`
    }
    if (type === 'link') {
      copy(
        `Just won a #prediction on #MojitoSwap! The prediction market is so much fun - let's try it together! Participate in MojitoSwap's prediction market and earn even more rewards! #DeFi #KCC Learn More: ${shareUrl}?code=${referralCode.current}`
      )
      toastInfo(t('UIKIT_14'))
    }
    if (url) {
      window.open(url, '_target')
    }
  }

  const renderPost = () => {
    return (
      <div>
        <PostContainer>
          <PostBg id="post-card">
            <Flex>
              <ImgLogo src={Imgs.logo} />
              <Divider />
              <Text bold fontSize="18px">
                PREDICTION
              </Text>
            </Flex>
            <Text fontSize="24px" mt={isMobile ? '33px' : '52px'} bold>
              {token.symbol}/USD
            </Text>
            <Text bold fontSize={isMobile ? '40px' : '52px'} color="primary">
              +{joinAmount ? new BN(total).minus(joinAmount).div(joinAmount).multipliedBy(100).toFixed(2, 0) : '-'} %
            </Text>
            <Text mt={isMobile ? '10px' : '27px'} color="textRemark">
              Round
            </Text>
            <Text bold fontSize={isMobile ? '24px' : '32px'} maxWidth="200px" style={{ wordBreak: 'break-all' }}>
              {isLoading ? (
                <Skeleton height="48" width="140px" />
              ) : epochs.length === 1 ? (
                epochs[0]
              ) : epochs.length > 5 ? (
                `${epochs.slice(0, 5).join(',')}...`
              ) : (
                epochs.join(', ')
              )}
            </Text>
            <Text mt="8px" color="textRemark">
              Reward
            </Text>
            <Text bold fontSize={isMobile ? '24px' : '32px'} color="primary">
              {`${formatNumber(total, 0, 4)} MJT`}
            </Text>
            <PostImgBg src={Imgs.bg} />
            <PostBottom>
              <QRCode
                value={`${shareUrl}?code=${referralCode.current || '-'}`}
                size={isMobile ? 80 : 97}
                style={{ padding: '3px', background: 'white' }}
              />
              <Flex ml={isMobile ? '15px' : '20px'} style={{ flexDirection: 'column' }}>
                <Text color="white" fontSize={isMobile ? '14px' : '16px'}>
                  Referral code:{' '}
                  <span style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold' }}>
                    {referralCode.current ?? '-'}
                  </span>
                </Text>
                <Text color="white" fontSize={isMobile ? '14px' : '16px'}>
                  Welcome to MojitoSwap! Scan the QR code to join the Mojito!
                </Text>
              </Flex>
            </PostBottom>
          </PostBg>
        </PostContainer>
        <ShareBox style={{ padding: isMobile ? '14px 28px 25px' : '28px 48px' }}>
          <Text width={isMobile ? 'auto' : '120px'} mb={isMobile ? '12px' : '0'}>
            Share on
          </Text>
          <Flex alignItems="center" justifyContent="space-between" width="100%">
            <ImgShare src={Imgs.download} onClick={() => exportImg()} />
            <ImgShare
              src={Imgs.twitter}
              onClick={() => {
                setShowPost(true)
                clickShare('twitter')
              }}
            />
            <TelegramShareButton
              url={`${shareUrl}?code=${referralCode.current}`}
              title={
                "Just won a #prediction on #MojitoSwap! The prediction market is so much fun - let's try it together! Participate in MojitoSwap's prediction market and earn even more rewards! #DeFi #KCC"
              }
            >
              <ImgShare
                src={Imgs.telegram}
                onClick={() => {
                  setShowPost(true)
                  clickShare('telegram')
                }}
              />
            </TelegramShareButton>
            <FacebookShareButton url={`${shareUrl}?code=${referralCode.current}`}>
              <ImgShare
                src={Imgs.facebook}
                onClick={() => {
                  setShowPost(true)
                  clickShare('facebook')
                }}
              />
            </FacebookShareButton>
          </Flex>
        </ShareBox>
      </div>
    )
  }

  return (
    <Modal minWidth="288px" position="relative" mt="124px">
      <ModalHeader>
        <ModalTitle>
          <Heading>{t('PREDICTION_32')}</Heading>
        </ModalTitle>
        <ModalCloseButton onDismiss={onDismiss} />
      </ModalHeader>
      {showPost ? (
        renderPost()
      ) : (
        <ModalBody p={isMobile ? '24px' : '36px 48px'}>
          <ImgCup src={require('assets/images/prediction/winner.png').default} />
          {/* <TrophyGoldIcon width="96px" mx="auto" mb="24px" /> */}
          <Flex flexDirection="column" alignItems="center" justifyContent="center">
            <Text bold fontSize="18px" mt="5px">
              {t('PREDICTION_31')}
            </Text>
            <Text fontSize="14px" color="textRemark" mt="24px">
              {t('PREDICTION_33')}
            </Text>
            <Text fontSize="20px" bold color="primary" mt="10px">{`${formatNumber(total, 0, 4)} MJT`}</Text>
            <Text fontSize="14px" color="textRemark" mt="12px">
              {epochs.length === 1 ? t('PREDICTION_34') : t('PREDICTION_35')}
            </Text>
            <Text fontSize="20px" bold mt="8px">
              {isLoading ? <Skeleton height="21" width="140px" /> : epochs.length === 1 ? epochs[0] : epochs.join(', ')}
            </Text>
          </Flex>
          <Button
            height="44px"
            width="100%"
            mt="28px"
            mb="8px"
            onClick={handleClick}
            isLoading={isPendingTx || isLoading}
            endIcon={isPendingTx ? <AutoRenewIcon spin color="currentColor" /> : null}
          >
            <Text fontSize="18px" color="white" bold>
              {t('PREDICTION_36')}
            </Text>
          </Button>
          <ShareBox>
            <Text width={isMobile ? 'auto' : '120px'} mb={isMobile ? '12px' : '0'}>
              Share on
            </Text>
            <Flex alignItems="center" justifyContent="space-between" width="100%">
              {showPost ? (
                <ImgShare src={Imgs.download} onClick={() => exportImg()} />
              ) : (
                <ImgShare
                  src={Imgs.link}
                  onClick={() => {
                    setShowPost(true)
                    clickShare('link')
                  }}
                />
              )}
              <ImgShare
                src={Imgs.twitter}
                onClick={() => {
                  setShowPost(true)
                  clickShare('twitter')
                }}
              />
              <TelegramShareButton
                url={`${shareUrl}?code=${referralCode.current}`}
                title={
                  "Just won a #prediction on #MojitoSwap! The prediction market is so much fun - let's try it together! Participate in MojitoSwap's prediction market and earn even more rewards! #DeFi #KCC"
                }
              >
                <ImgShare
                  src={Imgs.telegram}
                  onClick={() => {
                    setShowPost(true)
                    clickShare('telegram')
                  }}
                />
              </TelegramShareButton>
              <FacebookShareButton url={`${shareUrl}?code=${referralCode.current}`}>
                <ImgShare
                  src={Imgs.facebook}
                  onClick={() => {
                    setShowPost(true)
                    clickShare('facebook')
                  }}
                />
              </FacebookShareButton>
            </Flex>
          </ShareBox>
        </ModalBody>
      )}
    </Modal>
  )
}

export default CollectRoundWinningsModal
